import { DataType, Field, Table, Type } from 'apache-arrow';

export type FieldInfo = {
  name: string;
  typeId: number;
  typeName: string;
  typeObject: DataType;
  nullable: boolean;
}

export type RowValue = string | number | boolean | null;

export class ArrowHelper {
  static fieldToJson(field: Field): FieldInfo {
    return {
      name: field.name,
      typeId: field.type.typeId as number,
      typeName: Type[field.type.typeId as number],
      typeObject: field.type,
      nullable: field.nullable,
    }
  }

  static tableToJson(table: Table) {
    const vals = [];
    for (let i = 0; i < table.numCols; i++) {
      vals.push(this.columnValuesToJson(table, i, 0, table.numRows));
    }
    return {
      numRows: table.numRows,
      numCols: table.numCols,
      schema: table.schema.fields.map(f => ArrowHelper.fieldToJson(f)),
      columns: vals,
    }
  }

  /**
   * Convert an Arrow table to row-based JSON format.
   * Returns an array of objects where each object represents a row with column names as keys.
   *
   * Example output: [{"col1": val1, "col2": val2}, {"col1": val3, "col2": val4}]
   */
  static tableToRowJson(table: Table): Record<string, RowValue>[] {
    const rows: Record<string, RowValue>[] = [];
    const columnNames = table.schema.fields.map(f => f.name);
    const columnData: RowValue[][] = [];

    // Extract all column data first
    for (let i = 0; i < table.numCols; i++) {
      columnData.push(this.columnValuesToJson(table, i, 0, table.numRows));
    }

    // Convert from column-based to row-based format
    for (let rowIdx = 0; rowIdx < table.numRows; rowIdx++) {
      const row: Record<string, RowValue> = {};
      for (let colIdx = 0; colIdx < table.numCols; colIdx++) {
        row[columnNames[colIdx]] = columnData[colIdx][rowIdx];
      }
      rows.push(row);
    }

    return rows;
  }

  static tableRangeToJson(table: Table, offset: number, count: number) {
    if (offset < 0 || count < 0 || offset + count > table.numRows) {
      return null;
    }
    const vals = [];
    for (let i = 0; i < table.numCols; i++) {
      vals.push(this.columnValuesToJson(table, i, offset, count));
    }
    return {
      numRows: table.numRows,
      numCols: table.numCols,
      schema: table.schema.fields.map(f => ArrowHelper.fieldToJson(f)),
      columns: vals,
    }
  }

  static columnValuesToJson(table: Table, colIndex: number, offset: number, count: number): RowValue[] {
    const vector = table.getChildAt(colIndex);
    if (!vector) {
      return [];
    }
    const values: RowValue[] = [];
    for (let i = offset; i < offset + count; i++) {
      if (!vector.isValid(i)) {
        values.push(null);
        continue;
      }
      if (typeof vector.get(i) === 'bigint') {
        // Convert bigint to number for JSON compatibility
        // Note: This may lose precision for values > Number.MAX_SAFE_INTEGER (2^53-1)
        values.push(Number(vector.get(i)));
        continue;
      }
      if (DataType.isTimestamp(vector.type)) {
        values.push(new Date(vector.get(i)).toISOString());
        continue;
      }
      if (DataType.isInt(vector.type) || DataType.isFloat(vector.type)) {
        values.push(vector.get(i));
        continue;
      }
      values.push(vector.get(i).toString());
    }
    return values;
  }
}
